(function (global) {
  'use strict';

  // Public, dependency-free arithmetic API; the page enhancement below uses no network or storage.
  const PROTOCOL = Object.freeze({ id: 'EW-RSCH-AI-001', version: '1.0', dataset: 'INV-SYN-001' });
  const ENGLISH = Object.freeze({
    download_inventory: 'Download inventory CSV',
    download_guide: 'Download exercise guide',
    download_checklist: 'Download quality checklist',
    download_prompt: 'Download sample prompt',
    load_example: 'Load hypothetical example',
    clear_form: 'Clear form',
    comparison_setup: 'Comparison setup',
    workflow_code: 'Workflow code (optional)',
    codes_only: 'Use a code; do not enter names or confidential details.',
    rubric_version: 'Checklist version (optional)',
    run_order: 'Run order',
    order_matched: 'Separate matched tasks',
    order_baseline: 'Baseline first',
    order_assisted: 'Assisted first',
    labor_rate: 'Blended labor rate (USD/hour)',
    total_checks: 'Number of quality checks',
    minimum_quality: 'Minimum acceptable quality (%)',
    time_target: 'Time-reduction target (%)',
    baseline: 'Baseline',
    assisted: 'AI-assisted',
    execution_minutes: 'Execution (minutes)',
    review_minutes: 'Review (minutes)',
    correction_minutes: 'Corrections (minutes)',
    checks_passed: 'Quality checks passed',
    critical_errors: 'Critical errors',
    tool_cost: 'Tool cost for this run (USD)',
    support_cost: 'Setup/support share for this run (USD)',
    calculate: 'Calculate comparison',
    comparison_result: 'This comparison',
    total_time: 'Total time',
    time_change: 'Time change',
    estimated_cost: 'Estimated full cost',
    estimated_cost_difference: 'Estimated cost difference',
    quality_scores: 'Quality checks',
    critical_error_counts: 'Critical errors',
    add_comparison: 'Add comparison to this session',
    session_title: 'Your session comparisons',
    empty_session: 'No comparisons recorded in this session.',
    session_caption: 'Entries you added during this visit',
    record: 'Record',
    workflow: 'Workflow',
    data_type: 'Data type',
    decision: 'Decision',
    export_json: 'Export JSON',
    export_csv: 'Export CSV',
    clear_session: 'Clear session',
    clear_session_question: 'Clear every comparison in this session? Export first if you need to keep them.',
    confirm_clear: 'Yes, clear session',
    cancel: 'Cancel',
    hypothetical: 'Hypothetical example',
    adapted_hypothetical: 'Adapted hypothetical example',
    user_entered: 'User-entered comparison',
    hypothetical_note: 'Hypothetical example. These values are invented for demonstration and remain labeled in exports.',
    adapted_note: 'Adapted hypothetical example. Changes to the sample remain hypothetical. Clear the form to start a separately measured comparison.',
    error_heading: 'Check these entries before calculating:',
    error_number: '{field}: enter a finite, non-negative number no greater than {max}.',
    error_integer: '{field}: enter a whole number between {min} and {max}.',
    error_percent: '{field}: enter a percentage between 0 and 100.',
    error_passed: '{field}: checks passed cannot exceed the number of quality checks.',
    error_baseline_zero: 'Baseline: total execution, review, and correction time must be greater than zero.',
    error_text_length: '{field}: use no more than 48 characters.',
    error_order: 'Select one of the available run-order options.',
    target_met: 'Target met for this comparison',
    target_not_met: 'Time target not met for this comparison',
    quality_not_met: 'Quality threshold not met',
    pass_note: 'The assisted result meets the selected minimum quality, equals or exceeds baseline quality, and has no critical errors. This is a descriptive comparison of your entries.',
    fail_note: 'Time and cost calculations remain visible, but this comparison does not demonstrate an acceptable improvement.',
    minutes_pair: '{baseline} min baseline / {assisted} min assisted',
    cost_pair: '{baseline} baseline / {assisted} assisted',
    time_less: '{percent}% less time ({minutes} min released)',
    time_more: '{percent}% more time ({minutes} additional min)',
    time_equal: 'No change in total time',
    cost_less: '{amount} lower estimated cost',
    cost_more: '{amount} higher estimated cost',
    cost_equal: 'No change in estimated cost',
    quality_pair: '{baseline}/{total} baseline ({baselinePercent}%) / {assisted}/{total} assisted ({assistedPercent}%)',
    critical_pair: '{baseline} baseline / {assisted} assisted',
    changed: 'Inputs changed. Calculate again before adding a comparison.',
    calculated: 'Comparison calculated. {decision}.',
    form_cleared: 'Form cleared. Session comparisons have been kept.',
    added: 'Comparison {number} added to this session.',
    session_count: '{count} comparison(s) in this session. Export to keep a copy.',
    session_cleared: 'Session comparisons cleared. The form has been kept.',
    download_started: '{filename} download prepared.',
    no_records: 'Add a comparison before exporting.',
    not_specified: 'Not specified',
    record_limit: 'This session has reached 500 comparisons. Export them, then clear the session to continue.',
    export_notice: 'Session entries supplied by the user. Hypothetical records are explicitly marked. These are not published study findings.',
    resource_error: 'This browser could not prepare the download. You can copy the visible exercise and checklist.',
    export_error: 'This browser could not prepare the export. Keep this page open and try again.',
    enhancement_unavailable: "The protocol and exercise are available on this page. Interactive comparisons and downloads require the page's tools to load. If controls are unavailable, use the visible inventory, prompt, and checklist."
  });

  const CONDITIONS = ['baseline', 'assisted'];
  const MEASURES = ['execution', 'review', 'correction', 'passed', 'critical', 'tool', 'support'];
  const MEASURE_LABELS = {
    execution: 'execution_minutes', review: 'review_minutes', correction: 'correction_minutes',
    passed: 'checks_passed', critical: 'critical_errors', tool: 'tool_cost', support: 'support_cost'
  };
  const ORDERS = ['matched_tasks', 'baseline_first', 'assisted_first'];

  function number(value) {
    if (typeof value !== 'number' && typeof value !== 'string') return NaN;
    if (typeof value === 'string' && value.trim() === '') return NaN;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : NaN;
  }

  function validate(input) {
    const errors = [];
    const add = (field, key, params) => errors.push({ field, key, params: params || {} });
    const data = input && typeof input === 'object' ? input : {};
    const labor = number(data.laborRate);
    const checks = number(data.totalChecks);
    if (!Number.isFinite(labor) || labor < 0 || labor > 100000) add('labor_rate', 'error_number', { label: 'labor_rate', max: 100000 });
    if (!Number.isInteger(checks) || checks < 1 || checks > 10000) add('total_checks', 'error_integer', { label: 'total_checks', min: 1, max: 10000 });
    for (const [field, property, label] of [['minimum_quality', 'minQuality', 'minimum_quality'], ['time_target', 'timeTarget', 'time_target']]) {
      const value = number(data[property]);
      if (!Number.isFinite(value) || value < 0 || value > 100) add(field, 'error_percent', { label });
    }
    for (const condition of CONDITIONS) {
      const run = data[condition] || {};
      for (const measure of MEASURES) {
        const value = number(run[measure]);
        const field = condition + '_' + measure;
        const params = { condition, label: MEASURE_LABELS[measure], min: 0, max: measure === 'passed' ? 10000 : 1000000 };
        if (!Number.isFinite(value) || value < 0 || value > params.max) add(field, 'error_number', params);
        else if ((measure === 'passed' || measure === 'critical') && !Number.isInteger(value)) add(field, 'error_integer', params);
        else if (measure === 'passed' && Number.isInteger(checks) && value > checks) add(field, 'error_passed', params);
      }
    }
    if (data.baseline) {
      const total = number(data.baseline.execution) + number(data.baseline.review) + number(data.baseline.correction);
      if (Number.isFinite(total) && total === 0) add('baseline_execution', 'error_baseline_zero');
    }
    for (const [field, property, label] of [['workflow_code', 'workflowCode', 'workflow_code'], ['rubric_version', 'rubricVersion', 'rubric_version']]) {
      if (data[property] != null && (typeof data[property] !== 'string' || data[property].length > 48)) add(field, 'error_text_length', { label });
    }
    if (data.runOrder != null && !ORDERS.includes(data.runOrder)) add('run_order', 'error_order');
    return errors;
  }

  function calculate(input) {
    const errors = validate(input);
    if (errors.length) return { valid: false, errors };
    const laborRate = number(input.laborRate);
    const totalChecks = number(input.totalChecks);
    const summarize = (run) => {
      const totalMinutes = number(run.execution) + number(run.review) + number(run.correction);
      const laborCost = totalMinutes / 60 * laborRate;
      return {
        totalMinutes,
        qualityPercent: number(run.passed) / totalChecks * 100,
        laborCost,
        fullCost: laborCost + number(run.tool) + number(run.support)
      };
    };
    const baseline = summarize(input.baseline);
    const assisted = summarize(input.assisted);
    const minutesReleased = baseline.totalMinutes - assisted.totalMinutes;
    const timeReductionPercent = minutesReleased / baseline.totalMinutes * 100;
    const qualityGate = assisted.qualityPercent >= number(input.minQuality)
      && assisted.qualityPercent >= baseline.qualityPercent
      && number(input.assisted.critical) === 0;
    return {
      valid: true, baseline, assisted, minutesReleased, timeReductionPercent,
      costDifference: baseline.fullCost - assisted.fullCost,
      qualityGate,
      targetMet: qualityGate && timeReductionPercent >= number(input.timeTarget)
    };
  }

  // Quote every cell. Neutralize spreadsheet formulas in text, including leading whitespace/control characters.
  function csvCell(value) {
    let text = value == null ? '' : String(value);
    if (typeof value === 'string' && (/^[\s\u0000-\u001f]*[=+@\-]/.test(text) || /^[\t\r\n]/.test(text))) text = "'" + text;
    return '"' + text.replace(/"/g, '""') + '"';
  }
  function toCsv(rows) { return rows.map((row) => row.map(csvCell).join(',')).join('\r\n') + '\r\n'; }

  const INVENTORY = Object.freeze([
    ['SYN-101', 'Shipping cartons', 18, 30, 'each'],
    ['SYN-102', 'Label rolls', 12, 10, 'roll'],
    ['SYN-103', 'Packing tape', 6, 15, 'roll'],
    ['SYN-104', 'Safety gloves', 20, 20, 'pair'],
    ['SYN-105', 'Mailer envelopes', 0, 25, 'each'],
    ['SYN-106', 'Storage bins', 9, 8, 'each'],
    ['SYN-107', 'Printer paper', 4, 10, 'ream'],
    ['SYN-108', 'Cleaning cloths', 35, 15, 'each']
  ]);
  const SAMPLE_PROMPT = 'Use only the attached synthetic inventory CSV. Identify rows where on_hand is strictly less than reorder_threshold. Return a table containing SKU, item, on_hand, reorder_threshold, shortfall, and unit. Calculate shortfall as reorder_threshold minus on_hand. Do not convert units, invent missing values, or place orders. An item at its threshold is not an exception. If the data is ambiguous, state the ambiguity. State the number of exceptions and the total shortfall grouped by unit. Label the report "Synthetic practice only."';
  const CHECKLIST = [
    ['1', 'SYN-101 appears with a shortfall of 12 each.'],
    ['2', 'SYN-103 appears with a shortfall of 9 rolls.'],
    ['3', 'SYN-105 appears with a shortfall of 25 each.'],
    ['4', 'SYN-107 appears with a shortfall of 6 reams.'],
    ['5', 'SYN-102, SYN-106, and SYN-108 are excluded.'],
    ['6', 'SYN-104 is excluded because it is exactly at the threshold.'],
    ['7', 'The report states four exceptions.'],
    ['8', 'On-hand quantities and thresholds match the supplied rows.'],
    ['9', 'Grouped shortfalls are 37 each, 9 rolls, and 6 reams; unlike units are not combined.'],
    ['10', 'The report is labeled synthetic and makes no purchasing decision.']
  ];
  const CRITICAL_RULES = 'Critical errors: missing an exception; reporting an incorrect SKU or shortfall; combining incompatible units into a purchasing quantity; or claiming an order was placed. Count critical errors separately from checklist points.';
  const GUIDE = [
    'THE ABUNDANCE EXPERIMENT — SYNTHETIC INVENTORY EXERCISE',
    'Protocol EW-RSCH-AI-001 v1.0 | Dataset INV-SYN-001 v1.0 | 2026-10-27',
    '',
    'All data is invented for practice. No purchasing system is connected.',
    '',
    'TASK',
    'Identify rows with on_hand strictly below reorder_threshold. Shortfall equals reorder_threshold minus on_hand. An item exactly at its threshold is not an exception. Keep units distinct. Do not place orders or invent missing data.',
    '',
    'BASELINE',
    'Use your normal non-AI method. Time execution, review, and corrections separately. Score the final result with the checklist and count critical errors.',
    '',
    'ASSISTED',
    'Use an AI tool of your choice with the sample prompt and CSV. Verify every output row. Time execution, review, and corrections separately. Include tool cost and a documented setup/support allocation. Score with the same checklist.',
    '',
    'COMPARABILITY',
    'Repeating this identical dataset creates familiarity. Use it to learn the method. For a comparative pilot, use matched fresh tasks, alternate order where practical, record task difficulty and order, and set quality criteria before measurement. This practice exercise does not enroll you in a study.',
    '',
    'EXPECTED ANSWER',
    'SYN-101: 18 on hand, threshold 30, shortfall 12 each.',
    'SYN-103: 6 on hand, threshold 15, shortfall 9 rolls.',
    'SYN-105: 0 on hand, threshold 25, shortfall 25 each.',
    'SYN-107: 4 on hand, threshold 10, shortfall 6 reams.',
    'Four exceptions. Grouped shortfalls: 37 each, 9 rolls, 6 reams. No combined grand total across different units.',
    '',
    CRITICAL_RULES,
    '',
    'INITIAL DECISION RULE',
    'At least 20% less total time; assisted quality at least the chosen minimum and at least baseline; no assisted critical errors. The 20% threshold is proposed, not an established result.',
    '',
    'RECORDS',
    'Use anonymous workflow codes, never names, confidential records, or credentials. The website calculator keeps comparisons only in page memory; export them before leaving. Hypothetical examples remain labeled in exports.',
    '',
    'INTERPRETATION',
    'A labor valuation estimates the value of time, not automatic cash savings. A single practice run does not demonstrate a general research finding. Report unsuccessful runs, quality problems, and missing costs.'
  ].join('\n');

  function resources() {
    return {
      inventory: { filename: 'enoch-inventory-synthetic-v1.csv', type: 'text/csv;charset=utf-8', text: toCsv([['sku', 'item', 'on_hand', 'reorder_threshold', 'unit', 'dataset_id', 'is_synthetic'], ...INVENTORY.map((row) => [...row, PROTOCOL.dataset, true])]) },
      guide: { filename: 'enoch-abundance-exercise-guide-v1.txt', type: 'text/plain;charset=utf-8', text: GUIDE },
      checklist: { filename: 'enoch-inventory-quality-checklist-v1.txt', type: 'text/plain;charset=utf-8', text: ['INV-SYN-001 — SYNTHETIC INVENTORY CHECKLIST v1.0', 'Score one point per criterion met. Maximum: 10.', '', ...CHECKLIST.map((row) => row[0] + '. [ ] ' + row[1]), '', CRITICAL_RULES, '', 'Record total passed, total checks, and critical errors separately.'].join('\n') },
      prompt: { filename: 'enoch-inventory-sample-prompt-v1.txt', type: 'text/plain;charset=utf-8', text: 'INV-SYN-001 — SYNTHETIC PRACTICE PROMPT\n\n' + SAMPLE_PROMPT + '\n' }
    };
  }

  const previous = global.EnochAbundance;
  global.EnochAbundance = Object.freeze({ calculate, validate, csvCell, toCsv, resources, english: ENGLISH, protocol: PROTOCOL });
  if (typeof document === 'undefined' || (previous && previous.calculate)) return;

  function initialize() {
    const root = document.getElementById('ew-abundance-experiment');
    if (!root || root.dataset.ewaReady === 'true') return;
    const form = root.querySelector('#ewa-form');
    if (!form) return;
    root.dataset.ewaReady = 'true';

    let translations = {};
    const localeNode = document.getElementById('ew-abundance-translations');
    if (localeNode) {
      try {
        const parsed = JSON.parse(localeNode.textContent || '{}');
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) translations = parsed;
      } catch (_) { /* English fallbacks keep the tool usable if optional translations are malformed. */ }
    }
    const t = (key, params) => {
      let text = typeof translations[key] === 'string' ? translations[key] : (ENGLISH[key] || key);
      for (const [name, value] of Object.entries(params || {})) text = text.split('{' + name + '}').join(String(value));
      return text;
    };
    for (const node of root.querySelectorAll('[data-ewa-t]')) node.textContent = t(node.dataset.ewaT);
    const $ = (id) => root.querySelector('#' + id);
    const field = (name) => form.elements.namedItem(name);
    let numberFormat;
    let moneyFormat;
    try {
      const locale = document.documentElement.lang || 'en-US';
      numberFormat = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
      moneyFormat = new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
    } catch (_) {
      numberFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
      moneyFormat = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
    }
    const fmt = (value) => numberFormat.format(Math.abs(value) < 1e-10 ? 0 : value);
    const money = (value) => moneyFormat.format(Math.abs(value) < 1e-10 ? 0 : value);
    const records = [];
    let sampleMode = '';
    let current = null;
    let sequence = 0;

    function announce(message) { $('ewa-tool-status').textContent = message; }
    function decisionKey(result) { return !result.qualityGate ? 'quality_not_met' : (result.targetMet ? 'target_met' : 'target_not_met'); }
    function modeLabel(mode) { return t(mode || 'user_entered'); }
    function updateSampleLabel() {
      const node = $('ewa-example-state');
      node.hidden = !sampleMode;
      node.textContent = sampleMode ? t(sampleMode === 'hypothetical' ? 'hypothetical_note' : 'adapted_note') : '';
    }
    function clearErrors() {
      const node = $('ewa-form-errors');
      node.hidden = true;
      node.replaceChildren();
      for (const input of form.querySelectorAll('[aria-invalid]')) input.removeAttribute('aria-invalid');
    }
    function readInput() {
      const input = {
        workflowCode: field('workflow_code').value.trim(), rubricVersion: field('rubric_version').value.trim(),
        runOrder: field('run_order').value,
        laborRate: field('labor_rate').value, totalChecks: field('total_checks').value,
        minQuality: field('minimum_quality').value, timeTarget: field('time_target').value
      };
      for (const condition of CONDITIONS) {
        input[condition] = {};
        for (const measure of MEASURES) input[condition][measure] = field(condition + '_' + measure).value;
      }
      return input;
    }
    function numericSnapshot(input) {
      const output = {
        workflowCode: input.workflowCode, rubricVersion: input.rubricVersion, runOrder: input.runOrder,
        laborRate: number(input.laborRate), totalChecks: number(input.totalChecks),
        minQuality: number(input.minQuality), timeTarget: number(input.timeTarget)
      };
      for (const condition of CONDITIONS) {
        output[condition] = {};
        for (const measure of MEASURES) output[condition][measure] = number(input[condition][measure]);
      }
      return output;
    }
    function showErrors(errors) {
      const node = $('ewa-form-errors');
      const heading = document.createElement('strong');
      heading.textContent = t('error_heading');
      const list = document.createElement('ul');
      for (const error of errors) {
        const params = { ...error.params };
        params.field = (params.condition ? t(params.condition) + ' — ' : '') + (params.label ? t(params.label) : '');
        const item = document.createElement('li');
        item.textContent = t(error.key, params);
        list.append(item);
        const control = field(error.field);
        if (control) control.setAttribute('aria-invalid', 'true');
      }
      node.replaceChildren(heading, list);
      node.hidden = false;
      node.focus();
    }
    function timeChange(result) {
      if (Math.abs(result.minutesReleased) < 1e-10) return t('time_equal');
      return t(result.minutesReleased > 0 ? 'time_less' : 'time_more', {
        percent: fmt(Math.abs(result.timeReductionPercent)), minutes: fmt(Math.abs(result.minutesReleased))
      });
    }
    function renderResult(input, result) {
      $('ewa-result-decision').textContent = t(decisionKey(result)) + (sampleMode ? ' · ' + modeLabel(sampleMode) : '');
      $('ewa-quality-note').textContent = t(result.qualityGate ? 'pass_note' : 'fail_note');
      $('ewa-result-time').textContent = t('minutes_pair', { baseline: fmt(result.baseline.totalMinutes), assisted: fmt(result.assisted.totalMinutes) });
      $('ewa-result-time-change').textContent = timeChange(result);
      $('ewa-result-cost').textContent = t('cost_pair', { baseline: money(result.baseline.fullCost), assisted: money(result.assisted.fullCost) });
      $('ewa-result-cost-change').textContent = Math.abs(result.costDifference) < 1e-10 ? t('cost_equal') : t(result.costDifference > 0 ? 'cost_less' : 'cost_more', { amount: money(Math.abs(result.costDifference)) });
      $('ewa-result-quality').textContent = t('quality_pair', {
        baseline: input.baseline.passed, assisted: input.assisted.passed, total: input.totalChecks,
        baselinePercent: fmt(result.baseline.qualityPercent), assistedPercent: fmt(result.assisted.qualityPercent)
      });
      $('ewa-result-critical').textContent = t('critical_pair', { baseline: input.baseline.critical, assisted: input.assisted.critical });
      $('ewa-comparison-results').hidden = false;
      $('ewa-add-record').disabled = false;
    }
    function runCalculation() {
      clearErrors();
      const input = readInput();
      const result = calculate(input);
      current = null;
      $('ewa-add-record').disabled = true;
      if (!result.valid) {
        $('ewa-comparison-results').hidden = true;
        showErrors(result.errors);
        return;
      }
      current = { input: numericSnapshot(input), result, sampleMode };
      renderResult(current.input, result);
      announce(t('calculated', { decision: t(decisionKey(result)) }));
    }
    function invalidate() {
      const hadResult = current !== null || !$('ewa-comparison-results').hidden;
      current = null;
      $('ewa-add-record').disabled = true;
      $('ewa-comparison-results').hidden = true;
      clearErrors();
      if (sampleMode) sampleMode = 'adapted_hypothetical';
      updateSampleLabel();
      if (hadResult) announce(t('changed'));
    }
    form.addEventListener('submit', (event) => { event.preventDefault(); runCalculation(); });
    form.addEventListener('input', invalidate);
    form.addEventListener('change', invalidate);

    $('ewa-load-example').addEventListener('click', () => {
      form.reset();
      field('workflow_code').value = 'SYNTHETIC-DEMO';
      field('rubric_version').value = 'INV-SYN-001 v1';
      const sample = {
        baseline: { execution: 35, review: 10, correction: 5, passed: 9, critical: 0, tool: 0, support: 0 },
        assisted: { execution: 15, review: 12, correction: 3, passed: 9, critical: 0, tool: 0.5, support: 1 }
      };
      for (const condition of CONDITIONS) for (const measure of MEASURES) field(condition + '_' + measure).value = sample[condition][measure];
      sampleMode = 'hypothetical';
      updateSampleLabel();
      runCalculation();
    });
    $('ewa-clear-form').addEventListener('click', () => {
      form.reset();
      for (const condition of CONDITIONS) for (const measure of MEASURES) field(condition + '_' + measure).value = '';
      field('workflow_code').value = '';
      field('rubric_version').value = '';
      sampleMode = '';
      current = null;
      clearErrors();
      updateSampleLabel();
      $('ewa-comparison-results').hidden = true;
      $('ewa-add-record').disabled = true;
      announce(t('form_cleared'));
      field('workflow_code').focus();
    });

    function renderSession() {
      const rows = $('ewa-session-rows');
      rows.replaceChildren();
      for (const record of records) {
        const row = document.createElement('tr');
        const values = [record.record_id, record.input.workflowCode || t('not_specified'), modeLabel(record.data_type), timeChange(record.result), t(decisionKey(record.result))];
        values.forEach((value, index) => {
          const cell = document.createElement(index === 0 ? 'th' : 'td');
          if (index === 0) cell.scope = 'row';
          cell.textContent = String(value);
          row.append(cell);
        });
        rows.append(row);
      }
      const empty = records.length === 0;
      $('ewa-session-count').textContent = empty ? t('empty_session') : t('session_count', { count: records.length });
      $('ewa-session-table-wrap').hidden = empty;
      for (const id of ['ewa-export-json', 'ewa-export-csv', 'ewa-clear-session']) $(id).disabled = empty;
    }
    $('ewa-add-record').addEventListener('click', () => {
      if (!current) return;
      if (records.length >= 500) { announce(t('record_limit')); return; }
      sequence += 1;
      records.push({
        record_id: sequence, protocol_id: PROTOCOL.id, protocol_version: PROTOCOL.version,
        created_at: new Date().toISOString(), is_hypothetical: !!current.sampleMode,
        data_type: current.sampleMode || 'user_entered',
        input: current.input, result: current.result
      });
      current = null;
      $('ewa-add-record').disabled = true;
      renderSession();
      announce(t('added', { number: sequence }));
    });

    function download(filename, type, text) {
      const blob = new Blob([text], { type });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.hidden = true;
      root.append(anchor);
      anchor.click();
      anchor.remove();
      global.setTimeout(() => URL.revokeObjectURL(url), 10000);
      announce(t('download_started', { filename }));
    }
    function exportRows() {
      const headers = ['record_id', 'protocol_id', 'protocol_version', 'created_at_utc', 'is_hypothetical', 'data_type', 'workflow_code', 'rubric_version', 'run_order', 'labor_rate_usd_hour', 'quality_checks_total', 'minimum_quality_percent', 'time_target_percent'];
      for (const condition of CONDITIONS) for (const measure of MEASURES) headers.push(condition + '_' + measure + (['execution', 'review', 'correction'].includes(measure) ? '_minutes' : (['tool', 'support'].includes(measure) ? '_usd' : '')));
      headers.push('baseline_total_minutes', 'assisted_total_minutes', 'baseline_quality_percent', 'assisted_quality_percent', 'baseline_full_cost_usd', 'assisted_full_cost_usd', 'minutes_released', 'time_reduction_percent', 'estimated_cost_difference_usd', 'quality_gate_passed', 'target_met');
      const rows = records.map((record) => {
        const input = record.input;
        const result = record.result;
        const row = [record.record_id, record.protocol_id, record.protocol_version, record.created_at, record.is_hypothetical, record.data_type, input.workflowCode, input.rubricVersion, input.runOrder, input.laborRate, input.totalChecks, input.minQuality, input.timeTarget];
        for (const condition of CONDITIONS) for (const measure of MEASURES) row.push(input[condition][measure]);
        row.push(result.baseline.totalMinutes, result.assisted.totalMinutes, result.baseline.qualityPercent, result.assisted.qualityPercent, result.baseline.fullCost, result.assisted.fullCost, result.minutesReleased, result.timeReductionPercent, result.costDifference, result.qualityGate, result.targetMet);
        return row;
      });
      return [headers, ...rows];
    }
    $('ewa-export-json').addEventListener('click', () => {
      if (!records.length) { announce(t('no_records')); return; }
      try {
        download('enoch-abundance-comparisons-' + new Date().toISOString().slice(0, 10) + '.json', 'application/json;charset=utf-8', JSON.stringify({
          protocol_id: PROTOCOL.id, protocol_version: PROTOCOL.version, exported_at: new Date().toISOString(),
          currency: 'USD', notice: t('export_notice'), records
        }, null, 2) + '\n');
      } catch (_) { announce(t('export_error')); }
    });
    $('ewa-export-csv').addEventListener('click', () => {
      if (!records.length) { announce(t('no_records')); return; }
      try { download('enoch-abundance-comparisons-' + new Date().toISOString().slice(0, 10) + '.csv', 'text/csv;charset=utf-8', '\ufeff' + toCsv(exportRows())); }
      catch (_) { announce(t('export_error')); }
    });
    $('ewa-clear-session').addEventListener('click', () => { $('ewa-clear-confirm').hidden = false; $('ewa-cancel-clear').focus(); });
    $('ewa-cancel-clear').addEventListener('click', () => { $('ewa-clear-confirm').hidden = true; $('ewa-clear-session').focus(); });
    $('ewa-confirm-clear').addEventListener('click', () => {
      records.length = 0;
      $('ewa-clear-confirm').hidden = true;
      renderSession();
      announce(t('session_cleared'));
      $('ewa-session-title').setAttribute('tabindex', '-1');
      $('ewa-session-title').focus();
    });
    const resourceFiles = resources();
    for (const button of root.querySelectorAll('[data-ewa-download]')) {
      button.addEventListener('click', () => {
        const resource = resourceFiles[button.dataset.ewaDownload];
        if (!resource) return;
        try { download(resource.filename, resource.type, resource.text); }
        catch (_) { announce(t('resource_error')); }
      });
    }
    renderSession();
    // Reveal and enable the interactive surface only after every handler is attached.
    for (const node of form.querySelectorAll('fieldset[disabled]')) node.disabled = false;
    for (const node of root.querySelectorAll('[data-ewa-download], #ewa-load-example, #ewa-clear-form, #ewa-confirm-clear, #ewa-cancel-clear, #ewa-form button[type="submit"]')) node.disabled = false;
    for (const node of root.querySelectorAll('.ewa-enhanced')) node.hidden = false;
    const enhancementStatus = $('ewa-enhancement-status');
    if (enhancementStatus) enhancementStatus.hidden = true;
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
})(typeof window !== 'undefined' ? window : globalThis);
